import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

export default function Intelligence() {
  return (
    <div className="bg-black pt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="max-w-4xl mb-32"
        >
          <h1 className="text-6xl md:text-8xl font-serif font-bold mb-10 leading-[1.1]">Creative & Advertising Intelligence</h1>
          <p className="text-3xl text-zinc-400 font-light leading-relaxed">We transform raw market signals into strategic creative advantage.</p>
        </motion.div>

        <section className="py-24 border-t border-zinc-900">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
            <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight text-white">What Creative & Advertising Intelligence means for your brand.</h2>
            <div className="space-y-10 text-xl text-zinc-400 leading-relaxed font-light">
              <p>It is the process of systematic observation, strategic interpretation, and creative response. It moves marketing from guesswork to evidence-based execution.</p>
              <p>We monitor what your competitors are saying, how they are saying it, and where they are winning. Then we help you win bigger.</p>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-24 py-32 border-t border-zinc-900">
          <div>
            <h2 className="text-3xl font-serif font-bold mb-12 text-white">What We Monitor</h2>
            <ul className="space-y-6">
              {[
                'New advertising creatives and copy',
                'Adverts that remain visible across monitoring cycles',
                'Adverts that are discontinued or replaced',
                'Changes in offers and promotional messages',
                'Recurring hooks, audience angles and formats',
                'Landing pages and calls to action',
                'Category patterns and positioning opportunities'
              ].map((item, i) => (
                <li key={i} className="flex gap-4 text-lg text-zinc-300">
                  <SafeIcon name="Eye" className="w-6 h-6 text-blue-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-serif font-bold mb-12 text-white">What Clients Receive</h2>
            <ul className="space-y-6">
              {[
                'Secure client dashboard',
                'Scheduled competitor monitoring',
                'Intelligence alerts',
                'Creative, copy, offer and positioning analysis',
                'Opportunity and risk recommendations',
                'Executive intelligence report',
                'Strategy review',
                'Optional creative production'
              ].map((item, i) => (
                <li key={i} className="flex gap-4 text-lg text-zinc-300">
                  <SafeIcon name="CheckCircle" className="w-6 h-6 text-green-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-16 bg-zinc-900/30 rounded-[3rem] p-12 md:p-24 border border-zinc-800 mb-32">
          <h2 className="text-xs font-bold tracking-[0.4em] text-zinc-500 uppercase mb-12 text-center">Trust & Transparency</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              'Maxmark does not access competitors’ private advertising accounts.',
              'Maxmark does not promise exact private budgets or conversion results.',
              'Continued advert visibility is a signal, not automatic proof of profitability.',
              'Recommendations combine public evidence with strategic judgement.'
            ].map((text, i) => (
              <div key={i} className="flex gap-6">
                <SafeIcon name="Shield" className="w-8 h-8 text-blue-500 shrink-0" />
                <p className="text-sm text-zinc-400 leading-relaxed font-medium">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-32 text-center border-t border-zinc-900">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-10 text-white">Ready to see your market clearly?</h2>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/dashboard" className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-5 rounded font-bold uppercase tracking-widest text-xs transition-all active:scale-95 shadow-lg shadow-blue-600/10">Explore Live Portal Demo</Link>
            <Link to="/request-review" className="border border-zinc-800 hover:border-zinc-700 text-white px-12 py-5 rounded font-bold uppercase tracking-widest text-xs transition-all hover:bg-zinc-900/50">Request an Intelligence Review</Link>
            <Link to="/plans" className="border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white px-12 py-5 rounded font-bold uppercase tracking-widest text-xs transition-all hover:bg-zinc-900/50">View Subscriptions</Link>
          </div>
        </section>
      </div>
    </div>
  );
}